import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CartItem, Product, GiftCodeValidation } from '../types';
import { isLocalOnlyCategory } from '../lib/brandMap';
import { useDeliveryRegionStore } from './deliveryRegionStore';
import { useToastStore } from './toastStore';
// @module:cart-imports — 套餐形別 import 由 package-combo 注入於此行之下
import type { PackageConfigPublic } from '../types';

interface CartState {
  items: CartItem[];
  giftCode: GiftCodeValidation | null;
  customerNote: string;
  deliveryFee: number;
  addItem: (product: Product, quantity?: number) => void;
  // @module:cart-add-package-sig — 套餐加入簽名由 package-combo 注入於此行之下
  addPackage: (pkg: PackageConfigPublic, selections: { productId: number; productName: string | null; groupName: string }[], selectionText: string) => void;
  removeItem: (productId: number) => void;
  updateQuantity: (productId: number, quantity: number) => void;
  clearCart: () => void;
  setGiftCode: (giftCode: GiftCodeValidation | null) => void;
  setCustomerNote: (note: string) => void;
  setDeliveryFee: (fee: number) => void;
  totalItems: () => number;
  subtotal: () => number;
  discount: () => number;
  finalTotal: () => number;
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      giftCode: null,
      customerNote: '',
      deliveryFee: 0,

      addItem: (product, quantity = 1) => {
        // 地址為王（客人 2026-09-05）：收貨地區為海外時，「僅本地可售」品類不得加購
        if (useDeliveryRegionStore.getState().deliveryRegion === 'OS' && isLocalOnlyCategory(product.category)) {
          useToastStore.getState().addToast(`「${product.name}」只限香港本地配送，無法寄海外`, 'error');
          return;
        }
        set((state) => {
          const existing = state.items.find((i) => i.productId === product.id);
          if (existing) {
            return {
              items: state.items.map((i) =>
                i.productId === product.id
                  ? {
                      ...i,
                      quantity: i.quantity + quantity,
                      subtotal: (i.quantity + quantity) * i.unitPrice,
                    }
                  : i
              ),
            };
          }
          return {
            items: [
              ...state.items,
              {
                productId: product.id,
                name: product.name,
                quantity,
                unitPrice: product.price,
                subtotal: product.price * quantity,
                imageUrl: product.imageUrl,
                category: product.category,
              },
            ],
          };
        });
      },

      // @module:cart-add-package-impl — 套餐加入實現由 package-combo 注入於此行之下
      addPackage: (pkg, selections, selectionText) => {
        const key = -pkg.id; // 負數 sentinel：套餐 key 與真實產品 id 永不碰撞
        set((state) => {
          const existing = state.items.find(
            (i) => i.productId === key && i.selectionText === selectionText
          );
          if (existing) {
            return {
              items: state.items.map((i) =>
                i.productId === key && i.selectionText === selectionText
                  ? { ...i, quantity: i.quantity + 1, subtotal: (i.quantity + 1) * i.unitPrice }
                  : i
              ),
            };
          }
          return {
            items: [
              ...state.items,
              {
                productId: key,
                packageId: pkg.id,
                name: pkg.name,
                quantity: 1,
                unitPrice: pkg.priceCents,
                subtotal: pkg.priceCents,
                imageUrl: pkg.imageUrl || undefined,
                selectionText: selectionText || undefined,
                selections: selections.length ? selections : undefined,
              },
            ],
          };
        });
      },

      removeItem: (productId) => {
        set((state) => ({
          items: state.items.filter((i) => i.productId !== productId),
        }));
      },

      updateQuantity: (productId, quantity) => {
        if (quantity <= 0) {
          get().removeItem(productId);
          return;
        }
        set((state) => ({
          items: state.items.map((i) =>
            i.productId === productId
              ? { ...i, quantity, subtotal: quantity * i.unitPrice }
              : i
          ),
        }));
      },

      clearCart: () => set({ items: [], giftCode: null, customerNote: '', deliveryFee: 0 }),

      setGiftCode: (giftCode) => set({ giftCode }),

      setCustomerNote: (customerNote) => set({ customerNote }),

      setDeliveryFee: (deliveryFee) => set({ deliveryFee }),

      totalItems: () => {
        return get().items.reduce((sum, i) => sum + i.quantity, 0);
      },

      subtotal: () => {
        return get().items.reduce((sum, i) => sum + i.subtotal, 0);
      },

      discount: () => {
        const gift = get().giftCode;
        const subtotal = get().subtotal();
        if (!gift) return 0;
        return Math.min(gift.discountAmount, subtotal);
      },

      finalTotal: () => {
        return Math.max(0, get().subtotal() - get().discount() + get().deliveryFee);
      },
    }),
    {
      name: 'x1493-cart',
      partialize: (state) => ({ items: state.items, giftCode: state.giftCode, customerNote: state.customerNote }),
    }
  )
);
